(function () {
  'use strict';

  const endpoint = 'https://api.lionexcourier.com/api/v8/runcourier/unified-track';
  const messages = {
    invalid_reference: 'Enter a valid tracking number.',
    notfound: 'No shipment found for this tracking number.',
    unavailable: 'Tracking is temporarily unavailable.'
  };

  class TrackingError extends Error {
    constructor(code) {
      super(messages[code] || messages.unavailable);
      this.name = 'TrackingError';
      this.code = Object.hasOwn(messages, code) ? code : 'unavailable';
    }
  }

  const text = value => typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';

  function normalizeReference(value) {
    const reference = text(value).toUpperCase();
    if (!/^[A-Z0-9][A-Z0-9-]{0,63}$/.test(reference)) throw new TrackingError('invalid_reference');
    return reference;
  }

  function resolveCourier(reference, courier) {
    const normalizedReference = normalizeReference(reference);
    if (text(courier).toLowerCase() === 'prime') {
      if (/^\d{1,64}$/.test(normalizedReference)) return 'prime';
      throw new TrackingError('invalid_reference');
    }
    if (/^LE[A-Z0-9-]*$/.test(normalizedReference)) return 'leopards';
    if (/^\d{8}$/.test(normalizedReference)) return 'prime';
    if (/^\d{14}$/.test(normalizedReference)) return 'trax';
    throw new TrackingError('invalid_reference');
  }

  function adapterRequestUrl(value, reference) {
    let url;
    try { url = new URL(value); } catch { throw new TrackingError('unavailable'); }
    if (url.protocol !== 'https:' || url.username || url.password || url.hash) throw new TrackingError('unavailable');
    url.searchParams.set('tracking_number', reference);
    return url.href;
  }

  function abortError() {
    if (typeof DOMException !== 'undefined') return new DOMException('The operation was aborted.', 'AbortError');
    const error = new Error('The operation was aborted.');
    error.name = 'AbortError';
    return error;
  }

  function isNoInformation(body) {
    return body && typeof body === 'object' && text(body.error).toLowerCase().replace(/\.$/, '') === 'no tracking information found for this tracking number';
  }

  function sanitizePacket(packet, reference) {
    const events = Array.isArray(packet['Tracking Detail']) ? packet['Tracking Detail'].filter(event => event && typeof event === 'object' && !Array.isArray(event)).map(event => {
      const cityStatus = text(event.Status_With_City);
      return {
        status: text(event.Status) || cityStatus,
        city_status: cityStatus,
        datetime: text(event.Activity_datetime),
        reason: text(event.Reason)
      };
    }).filter(event => event.status) : [];
    const rawStatus = text(packet.booked_packet_status);
    if (!rawStatus && !events.length) throw new TrackingError('unavailable');
    return {
      tracking: text(packet.track_number) || text(packet.track_number_short) || reference,
      provider: 'LionEx',
      partner: text(packet.courier),
      raw_status: rawStatus,
      origin_city: text(packet.origin_city_name),
      destination_city: text(packet.destination_city_name),
      booking_date: text(packet.booking_date),
      events,
      is_live: true,
      fetched_at: new Date().toISOString()
    };
  }

  function sanitizeAdapterRecord(body, reference, courier) {
    const courierName = courier === 'prime' ? 'Prime' : 'Trax';
    const isObject = value => value && typeof value === 'object' && !Array.isArray(value);
    const field = (value, maximum = 500) => {
      if (value === undefined || value === null) return '';
      if (typeof value !== 'string' || value.length > maximum) throw new TrackingError('unavailable');
      return value.trim();
    };
    const timestamp = value => {
      const source = field(value, 19);
      const match = source.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
      if (!match) throw new TrackingError('unavailable');
      const parts = match.slice(1).map(Number);
      const [year, month, day, hour, minute, second] = parts;
      const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
      if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day || date.getUTCHours() !== hour || date.getUTCMinutes() !== minute || date.getUTCSeconds() !== second) throw new TrackingError('unavailable');
      return source.replace('T', ' ');
    };
    if (!isObject(body) || body.tracking !== reference || field(body.partner, 32).toLowerCase() !== courier || !Array.isArray(body.events) || body.events.length > 500) throw new TrackingError('unavailable');
    const events = body.events.map(event => {
      if (!isObject(event)) throw new TrackingError('unavailable');
      const status = field(event.status, 240);
      if (!status) throw new TrackingError('unavailable');
      return { status, city_status: field(event.city_status, 240), datetime: timestamp(event.datetime), reason: field(event.reason) };
    });
    const rawStatus = field(body.raw_status, 240);
    if (!rawStatus && !events.length) throw new TrackingError('unavailable');
    return {
      tracking: reference,
      provider: courierName,
      partner: courierName,
      raw_status: rawStatus,
      origin_city: field(body.origin_city, 100),
      destination_city: field(body.destination_city, 100),
      booking_date: field(body.booking_date, 64),
      events,
      is_live: true,
      fetched_at: new Date().toISOString()
    };
  }

  async function lookupTracking(value, { signal, fetchImpl = globalThis.fetch, timeoutMs = 15000, traxEndpoint, primeEndpoint, courier: selectedCourier } = {}) {
    const reference = normalizeReference(value);
    const courier = resolveCourier(reference, selectedCourier);
    const useAdapter = courier === 'trax' || courier === 'prime';
    if (signal?.aborted) throw abortError();
    if (typeof fetchImpl !== 'function') throw new TrackingError('unavailable');
    const requestUrl = useAdapter ? adapterRequestUrl(courier === 'prime' ? primeEndpoint : traxEndpoint, reference) : endpoint;

    const controller = new AbortController();
    let timedOut = false;
    let timer;
    let onAbort;
    const cancel = () => controller.abort();
    const aborted = new Promise((resolve, reject) => {
      onAbort = () => reject(timedOut ? new TrackingError('unavailable') : abortError());
      controller.signal.addEventListener('abort', onAbort, { once: true });
    });
    signal?.addEventListener('abort', cancel, { once: true });
    const duration = Number.isFinite(timeoutMs) && timeoutMs >= 0 ? timeoutMs : 15000;
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, duration);

    try {
      return await Promise.race([(async () => {
        const response = await fetchImpl(requestUrl, {
          method: useAdapter ? 'GET' : 'POST',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          ...(useAdapter ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ trackingNumber: reference }) }),
          signal: controller.signal
        });
        let body;
        try {
          body = await response.json();
        } catch {
          throw new TrackingError('unavailable');
        }
        if (useAdapter) {
          if (!response.ok) throw new TrackingError(response.status === 404 ? 'notfound' : 'unavailable');
          return sanitizeAdapterRecord(body, reference, courier);
        }
        if (!response.ok) {
          if ([400, 404].includes(response.status) && isNoInformation(body)) throw new TrackingError('notfound');
          throw new TrackingError('unavailable');
        }
        if (!body || typeof body !== 'object' || !Array.isArray(body.packet_list)) throw new TrackingError('unavailable');
        if (!body.packet_list.length) throw new TrackingError('notfound');
        const packets = body.packet_list.filter(packet => packet && typeof packet === 'object' && !Array.isArray(packet));
        const matching = packets.filter(packet => [packet.track_number, packet.track_number_short].some(number => text(number).toUpperCase() === reference));
        const packet = matching.length === 1 ? matching[0] : packets.length === 1 && body.packet_list.length === 1 ? packets[0] : null;
        if (!packet) throw new TrackingError('unavailable');
        return sanitizePacket(packet, reference);
      })(), aborted]);
    } catch (error) {
      if (signal?.aborted) throw abortError();
      if (error instanceof TrackingError) throw error;
      throw new TrackingError('unavailable');
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
      controller.signal.removeEventListener('abort', onAbort);
    }
  }

  const api = { lookupTracking, normalizeReference, resolveCourier, TrackingError };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else window.TTLionExClient = api;
})();
